import {
    ButtonGradientPrimary,
    Colors,
    Heading,
    Icon,
    SegmentedProgressBar,
    Text,
} from '@amelio/ui-library';

import {
    EVALUATION_PANEL_INNER_SURFACE,
    EVALUATION_PANEL_TITLE,
    EvaluationPanel,
} from '../Evaluations/EvaluationPanel';
import { useEmployeeObjectivesInPeriod } from '../ObjectivesTab/useEmployeeObjectivesInPeriod';

import PeriodSelect from '@/components/objectives/PeriodSelect';
import { useAmelioTranslations } from '@/hooks';

interface ObjectivesAchievementCardProps {
    employeeId: string;
    /** `undefined` while the employee is still loading: no sentence is written without a name. */
    employeeFirstName: string | undefined;
    /** The profile's period lens, shared with the Objectives tab. */
    pickedPeriodId: string | null;
    onPickPeriod: (periodId: string) => void;
    onViewAllObjectives: () => void;
    className?: string;
}

/**
 * The assessment tab's objectives summary: the period lens, what the employee finished in that
 * period as a sentence and a segmented bar, and a way to the Objectives tab. With no objective in
 * the period there is no figure at all: a 0 % under someone's name would claim they achieved
 * nothing.
 *
 * The sentence names both figures -- how many objectives are finished, and how far they have come
 * on average -- because the Objectives tab shows the second one. Said of the first alone, "achieved
 * 0 %" reads as no progress at all while the tab reports 23 %.
 *
 * The bar draws the second figure, the one the sentence ends on: it is the progression, and a bar
 * left empty beside a sentence reporting 34 % contradicts it.
 */
export const ObjectivesAchievementCard = ({
    employeeId,
    employeeFirstName,
    pickedPeriodId,
    onPickPeriod,
    onViewAllObjectives,
    className,
}: ObjectivesAchievementCardProps) => {
    const { t } = useAmelioTranslations();
    const {
        periodOptions,
        periodId,
        selectedPeriod,
        isLoadingPeriods,
        summary,
        isSummaryKnown,
        isError,
    } = useEmployeeObjectivesInPeriod({ employeeId, pickedPeriodId });

    const canDescribe = isSummaryKnown && employeeFirstName !== undefined;
    const hasObjectives = canDescribe && summary.total > 0;
    const sentenceValues = {
        name: employeeFirstName,
        period: selectedPeriod?.label,
        // `count` drives the plural of "objectif"; the two figures are named apart from it.
        count: summary.total,
        achieved: summary.achieved,
        overall: summary.overall,
    };

    const describeAchievement = () => {
        if (isError) {
            return t('performanceProfile.assessmentObjectives.loadError');
        }
        if (!canDescribe) {
            return t('performanceProfile.loading');
        }
        if (hasObjectives && selectedPeriod) {
            return t('performanceProfile.assessmentObjectives.completedInPeriod', sentenceValues);
        }
        if (hasObjectives) {
            return t('performanceProfile.assessmentObjectives.completedAllPeriods', sentenceValues);
        }
        if (selectedPeriod) {
            return t(
                'performanceProfile.assessmentObjectives.noObjectivesInPeriod',
                sentenceValues,
            );
        }
        return t('performanceProfile.assessmentObjectives.noObjectives', sentenceValues);
    };

    return (
        <EvaluationPanel
            title={t('performanceProfile.assessmentObjectives.title')}
            className={className}
            dataTestId={'objectives-achievement-card'}
        >
            {(isLoadingPeriods || periodOptions.length > 0) && (
                <div className={'self-end'}>
                    <PeriodSelect
                        options={periodOptions}
                        value={periodId}
                        onChange={onPickPeriod}
                        isLoading={isLoadingPeriods}
                    />
                </div>
            )}

            <div className={`${EVALUATION_PANEL_INNER_SURFACE} flex flex-col gap-(--gap-gap-2xl)`}>
                <div className={'flex flex-col gap-(--gap-gap-lg)'}>
                    <Heading
                        level={4}
                        color={Colors.NAVY}
                        className={EVALUATION_PANEL_TITLE}
                    >
                        {t('performanceProfile.assessmentObjectives.achievementTitle')}
                    </Heading>
                    <Text
                        color={Colors.BASE_500}
                        className={'text-body-sm'}
                    >
                        {describeAchievement()}
                    </Text>
                </div>

                {hasObjectives && (
                    <SegmentedProgressBar
                        value={summary.overall}
                        ariaLabel={t('performanceProfile.assessmentObjectives.rateLabel')}
                    />
                )}

                {/* About 36px tall from its padding: the 21px line of size md plus py-2 above and below. */}
                <ButtonGradientPrimary
                    size={'md'}
                    className={'fit-content h-auto self-start py-2'}
                    onClick={onViewAllObjectives}
                    leftIcon={
                        <Icon
                            name={'essential-target'}
                            color={Colors.WHITE}
                            size={16}
                            ariaHidden
                        />
                    }
                >
                    {t('performanceProfile.assessmentObjectives.viewAll')}
                </ButtonGradientPrimary>
            </div>
        </EvaluationPanel>
    );
};
